import { Badge, Divider, Modal, ScrollArea, Stack, Text } from "@mantine/core"
import { AppLanguageMenu, Button, EqualWidthGrid, TextArea } from "@tfg/ui"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import z from "zod"

import {
  ProductSheetLanguage,
  ProductSheetLanguages,
} from "@tfg/types/constants/language.constants"
import {
  ContentBlockWithTextContent,
  ContentWithTextContent,
  ContentWithTextContentSchema,
} from "@tfg/types/schemas/communication-studio/components"
import { Locale } from "@tfg/types/schemas/enum"
import { ProductSheetLanguageSchema } from "@tfg/types/schemas/languages"

import { queryClient } from "~/backend/queryClient"
import {
  getProductPageComponentsQueryKey,
  GetProductPageComponentsResponseDto,
} from "~/gen/api"
import { useUpdateProductPageComponentsContent } from "~/gen/api/hooks/useUpdateProductPageComponentsContent"
import { useGetCurrentLanguage } from "~/pages/Brand/CommunicationStudio/Languages/utils/productSheetLanguages"
import { useOrganizationSettings } from "~/pages/Brand/CommunicationStudio/contexts/organizationSettings/useOrganizationSettings"
import { findLanguage, findLanguageFlag } from "~/utils/languages"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

interface Props {
  selectedComponent: GetProductPageComponentsResponseDto
  onClose: () => void
  isOpen: boolean
}

export const EditProductComponentModal = ({
  selectedComponent,
  onClose,
  isOpen,
}: Props) => {
  const { t } = useTranslation("communicationStudio")
  const initialLanguage = useGetCurrentLanguage() ?? ProductSheetLanguages.EN
  const [language, setLanguage] =
    useState<ProductSheetLanguage>(initialLanguage)
  const { displaySettingsId, organization } = useOrganizationSettings()
  const { mutate: updateProductPageComponentContent } =
    useUpdateProductPageComponentsContent({
      mutation: {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: getProductPageComponentsQueryKey(displaySettingsId ?? ""),
          })
          await queryClient.invalidateQueries({
            queryKey: ["organization", organization.id],
          })
          onClose()
          showSuccessNotification({
            title: t("tabs.content.editProductComponentModal.success.title"),
            message: t(
              "tabs.content.editProductComponentModal.success.message"
            ),
          })
        },
        onError: (error) => {
          showAxiosError({
            error,
            showNotification: true,
          })
        },
      },
    })

  const [content, setContent] = useState<ContentBlockWithTextContent>(() => {
    const firstLanguage = ProductSheetLanguageSchema.parse(
      Object.keys(selectedComponent.content)[0]
    )
    const baseBlocks: ContentWithTextContent[] =
      (selectedComponent.content[firstLanguage] as ContentWithTextContent[]) ||
      []
    return Object.keys(ProductSheetLanguages)
      .map((lang) => ProductSheetLanguageSchema.parse(lang))
      .reduce<ContentBlockWithTextContent>((acc, lang) => {
        const content = z
          .array(ContentWithTextContentSchema)
          .safeParse(selectedComponent.content[lang])
        if (!content.success) {
          return acc
        }
        acc[lang] = content.data

        if (!acc[lang] || acc[lang].length === 0) {
          acc[lang] = baseBlocks.map((block) => {
            return {
              type: block.type,
              textContent:
                block.textContent?.map(({ text, ...rest }) => ({
                  text: "",
                  ...rest,
                })) || [],
            }
          })
        }
        return acc
      }, {})
  })

  const onSubmit = () => {
    updateProductPageComponentContent({
      data: {
        productPageComponentId: selectedComponent.id,
        content,
        configurations: selectedComponent.configurations,
      },
    })
  }

  return (
    <Modal opened={isOpen} onClose={onClose} title="Edit component">
      <form onSubmit={onSubmit}>
        <Stack gap="xs">
          <AppLanguageMenu
            currentLocale={language}
            handleLanguageChange={(value) =>
              setLanguage(value.toLocaleUpperCase() as ProductSheetLanguage)
            }
            languageItems={
              Object.values(ProductSheetLanguages).map((language) => ({
                label: findLanguage(language),
                value: language as Locale,
                icon: findLanguageFlag(language as Locale),
              })) || []
            }
          />
          <Divider />
          <ScrollArea h={300}>
            {content[language]?.map((text, blockIndex) => {
              return (
                <Stack key={`${text.type}-${blockIndex}`} gap="xs">
                  <Text fw="bold">{text.type}</Text>
                  <Stack>
                    {text.textContent?.map((textContent, textIndex) => {
                      return (
                        <TextArea
                          key={`${textContent.type}-${textIndex}`}
                          value={textContent.text}
                          onChange={(e) => {
                            const newContent = structuredClone(
                              content
                            ) as ContentBlockWithTextContent
                            if (
                              newContent[language]?.[blockIndex]?.textContent?.[
                                textIndex
                              ]
                            ) {
                              newContent[language][blockIndex].textContent[
                                textIndex
                              ].text = e.target.value
                              setContent(newContent)
                            }
                          }}
                          label={
                            <Badge size="xs">{`${textContent.type} `}</Badge>
                          }
                        />
                      )
                    })}
                  </Stack>
                </Stack>
              )
            })}
          </ScrollArea>
          <EqualWidthGrid>
            <Button fullWidth onClick={() => onSubmit()}>
              Save
            </Button>
            <Button fullWidth variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          </EqualWidthGrid>
        </Stack>
      </form>
    </Modal>
  )
}
